import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Loader2,
  LockKeyhole,
  ShieldCheck,
  User,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

import ChaafoLogo from "../Images/Chaafo_Logo.png";
import { useProfile } from "../Services/Profile/useProfile";

const getResponseObject = (value) => {
  if (Array.isArray(value)) {
    return value[0] || {};
  }

  if (value && typeof value === "object") {
    return value;
  }

  return {};
};

const getStoredUser = () => {
  try {
    const storedUser = sessionStorage.getItem("customer");

    return storedUser ? JSON.parse(storedUser) : {};
  } catch (error) {
    console.error("Stored user parse error:", error);

    sessionStorage.removeItem("customer");

    return {};
  }
};

const getUserCode = (user) => {
  return String(
    user?.user_code ||
      user?.userCode ||
      user?.userid ||
      user?.userId ||
      user?.customer_code ||
      "",
  ).trim();
};

const getFirstName = (user) => {
  return String(user?.first_name || user?.firstName || "").trim();
};

const getLastName = (user) => {
  return String(user?.last_name || user?.lastName || "").trim();
};

const getPrivacyPolicy = (user) => {
  return String(
    user?.privacy_policy || user?.privacyPolicy || "N",
  ).toUpperCase();
};

const ProfileDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    insertProfileDetails,
    profileLoading,
    profileError,
    setProfileError,
  } = useProfile();

  const initialUser = useMemo(() => {
    const navigationUser =
      location.state?.user || location.state?.profile || {};

    const storedUser = getStoredUser();

    return {
      ...storedUser,
      ...navigationUser,
    };
  }, [location.state]);

  const userCode = useMemo(() => {
    return (
      String(location.state?.userCode || "").trim() || getUserCode(initialUser)
    );
  }, [initialUser, location.state]);

  const [firstName, setFirstName] = useState(getFirstName(initialUser));

  const [lastName, setLastName] = useState(getLastName(initialUser));

  const [privacyAccepted, setPrivacyAccepted] = useState(
    getPrivacyPolicy(initialUser) === "Y",
  );

  const [successMessage, setSuccessMessage] = useState("");

  const [localError, setLocalError] = useState("");

  useEffect(() => {
    if (!userCode) {
      setLocalError("User code is missing. Please login again.");
    }
  }, [userCode]);

  const clearMessages = () => {
    setLocalError("");
    setProfileError("");
    setSuccessMessage("");
  };

  const handleFirstNameChange = (event) => {
    const value = event.target.value.replace(/[^a-zA-Z\s'-]/g, "").slice(0, 40);

    setFirstName(value);
    clearMessages();
  };

  const handleLastNameChange = (event) => {
    const value = event.target.value.replace(/[^a-zA-Z\s'-]/g, "").slice(0, 40);

    setLastName(value);
    clearMessages();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    clearMessages();

    const cleanFirstName = firstName.trim();
    const cleanLastName = lastName.trim();

    if (!userCode) {
      setLocalError("User code is missing. Please login again.");
      return;
    }

    if (!cleanFirstName) {
      setLocalError("Please enter your first name.");
      return;
    }

    if (!cleanLastName) {
      setLocalError("Please enter your last name.");
      return;
    }

    if (!privacyAccepted) {
      setLocalError("Please accept the Privacy Policy to continue.");
      return;
    }

    try {
      const response = await insertProfileDetails({
        userCode,
        firstName: cleanFirstName,
        lastName: cleanLastName,
        privacyPolicy: "Y",
      });

      if (String(response?.responseCode) !== "00") {
        setLocalError(
          response?.responseMessage || "Unable to save profile details.",
        );
        return;
      }

      const responseProfile = getResponseObject(response?.response);

      const completedProfile = {
        ...initialUser,
        ...responseProfile,

        user_code:
          responseProfile?.user_code || responseProfile?.userCode || userCode,

        first_name:
          responseProfile?.first_name ||
          responseProfile?.firstName ||
          cleanFirstName,

        last_name:
          responseProfile?.last_name ||
          responseProfile?.lastName ||
          cleanLastName,

        privacy_policy: "Y",
      };

      sessionStorage.setItem("customer", JSON.stringify(completedProfile));

      setSuccessMessage(
        response?.responseMessage || "Profile completed successfully.",
      );

      navigate("/", {
        replace: true,
      });
    } catch (error) {
      setLocalError(error.message);
    }
  };

  const visibleError = localError || profileError;

  return (
    <main className="relative min-h-[100dvh] overflow-x-hidden bg-[#f5f3ef] font-[Inter]">
      {/* Background effects */}
      <div className="pointer-events-none absolute -left-28 -top-28 h-72 w-72 rounded-full bg-[#ff6b35]/10 blur-3xl" />

      <div className="pointer-events-none absolute -bottom-32 -right-28 h-80 w-80 rounded-full bg-[#111827]/10 blur-3xl" />

      <div className="relative mx-auto flex min-h-[100dvh] w-full max-w-[1400px] items-stretch justify-center sm:items-center sm:p-5 lg:p-8">
        <div className="grid min-h-[100dvh] w-full max-w-[1080px] overflow-hidden bg-white sm:min-h-0 sm:rounded-[28px] sm:border sm:border-white/70 sm:shadow-[0_35px_100px_rgba(15,23,42,0.14)] lg:min-h-[680px] lg:grid-cols-[0.95fr_1.05fr]">
          {/* Left premium section */}
          <section className="relative hidden overflow-hidden bg-[#111827] p-12 text-white lg:flex lg:flex-col lg:justify-between">
            <div className="absolute -left-24 -top-24 h-72 w-72 rounded-full bg-[#ff6b35]/25 blur-3xl" />

            <div className="absolute -bottom-32 -right-24 h-96 w-96 rounded-full bg-[#ffb703]/10 blur-3xl" />

            <div className="relative z-10">
              <button
                type="button"
                onClick={() => navigate("/")}
                className="flex items-center gap-3"
              >
                <span className="flex h-[72px] w-[72px] items-center justify-center rounded-[22px] border border-white/20 bg-white shadow-xl">
                  <img
                    src={ChaafoLogo}
                    alt="Chaafo Logo"
                    className="h-[56px] w-[56px] object-contain"
                  />
                </span>

                <div className="text-left">
                  <p className="text-[22px] font-extrabold">Chaafo</p>

                  <p className="text-xs text-white/55">
                    Food, delivered better
                  </p>
                </div>
              </button>

              <div className="mt-16">
                <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-semibold text-white/80">
                  <ShieldCheck size={15} className="text-[#ffb703]" />
                  Secure profile setup
                </div>

                <h1 className="mt-7 max-w-[430px] text-[46px] font-extrabold leading-[1.08] tracking-[-0.04em]">
                  One last step.
                  <span className="block text-[#ff8a4c]">Make it yours.</span>
                </h1>

                <p className="mt-6 max-w-[420px] text-[16px] leading-8 text-white/65">
                  Complete your profile to enjoy faster ordering, personalised
                  recommendations and a smoother checkout experience.
                </p>
              </div>
            </div>

            <div className="relative z-10 rounded-[22px] border border-white/10 bg-white/[0.07] p-5">
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#ff6b35]/20">
                  <LockKeyhole size={20} className="text-[#ff8a4c]" />
                </div>

                <div>
                  <p className="text-sm font-semibold">
                    Your details stay secure
                  </p>

                  <p className="mt-1 text-xs leading-5 text-white/50">
                    Your profile information is used only to improve your Chaafo
                    experience.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Profile form */}
          <section className="relative flex min-h-[100dvh] items-start justify-center overflow-y-auto px-4 pb-8 pt-[76px] min-[375px]:px-5 sm:min-h-[680px] sm:items-center sm:px-10 sm:py-16 lg:min-h-0 lg:px-16">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="absolute left-4 top-4 flex h-10 w-10 items-center justify-center rounded-full border border-[#e5e7eb] bg-white text-[#374151] shadow-sm transition hover:bg-[#f8fafc] sm:left-7 sm:top-7 sm:h-11 sm:w-11"
              aria-label="Go back"
            >
              <ArrowLeft size={19} />
            </button>

            <div className="w-full max-w-[470px]">
              {/* Mobile logo */}
              <div className="mb-6 flex flex-col items-center lg:hidden">
                <div className="flex h-[76px] w-[76px] items-center justify-center rounded-[22px] border border-[#ece7df] bg-white shadow-[0_12px_30px_rgba(15,23,42,0.09)]">
                  <img
                    src={ChaafoLogo}
                    alt="Chaafo Logo"
                    className="h-[60px] w-[60px] object-contain"
                  />
                </div>

                <p className="mt-2 text-lg font-extrabold text-[#111827]">
                  Chaafo
                </p>
              </div>

              <div className="mb-7">
                <div className="inline-flex items-center gap-2 rounded-full bg-[#fff3ec] px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.1em] text-[#ef5b2a]">
                  <User size={14} />
                  Profile details
                </div>

                <h2 className="mt-4 text-[29px] font-extrabold leading-[1.12] tracking-[-0.03em] text-[#111827] min-[375px]:text-[32px] sm:text-[40px]">
                  Complete your profile
                </h2>

                <p className="mt-3 text-[13px] leading-6 text-[#6b7280] min-[375px]:text-[14px] sm:text-[15px] sm:leading-7">
                  Enter your name and accept our Privacy Policy to complete your
                  account.
                </p>
              </div>

              {!userCode ? (
                <div className="rounded-[16px] border border-red-200 bg-red-50 p-4">
                  <p className="text-sm font-semibold text-red-700">
                    User code is missing. Please login again.
                  </p>

                  <button
                    type="button"
                    onClick={() =>
                      navigate("/login", {
                        replace: true,
                      })
                    }
                    className="mt-4 flex h-11 w-full items-center justify-center rounded-xl bg-[#111827] text-sm font-bold text-white"
                  >
                    Go to login
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label
                        htmlFor="first-name"
                        className="mb-2 block text-[13px] font-bold text-[#374151] sm:text-sm"
                      >
                        First name
                      </label>

                      <div className="relative">
                        <User
                          size={18}
                          className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9ca3af]"
                        />

                        <input
                          id="first-name"
                          type="text"
                          autoComplete="given-name"
                          value={firstName}
                          onChange={handleFirstNameChange}
                          placeholder="First name"
                          className="h-[56px] w-full rounded-[15px] border border-[#dfe3e8] bg-white pl-11 pr-4 text-[15px] font-medium text-[#111827] outline-none transition placeholder:font-normal placeholder:text-[#a2a8b1] focus:border-[#ff6b35] focus:ring-4 focus:ring-[#ff6b35]/10"
                        />
                      </div>
                    </div>

                    <div>
                      <label
                        htmlFor="last-name"
                        className="mb-2 block text-[13px] font-bold text-[#374151] sm:text-sm"
                      >
                        Last name
                      </label>

                      <div className="relative">
                        <User
                          size={18}
                          className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9ca3af]"
                        />

                        <input
                          id="last-name"
                          type="text"
                          autoComplete="family-name"
                          value={lastName}
                          onChange={handleLastNameChange}
                          placeholder="Last name"
                          className="h-[56px] w-full rounded-[15px] border border-[#dfe3e8] bg-white pl-11 pr-4 text-[15px] font-medium text-[#111827] outline-none transition placeholder:font-normal placeholder:text-[#a2a8b1] focus:border-[#ff6b35] focus:ring-4 focus:ring-[#ff6b35]/10"
                        />
                      </div>
                    </div>
                  </div>

                  <label className="mt-5 flex cursor-pointer items-start gap-3 rounded-[16px] border border-[#e5e7eb] bg-[#f9fafb] p-4 transition hover:border-[#ff6b35]/40">
                    <input
                      type="checkbox"
                      checked={privacyAccepted}
                      onChange={(event) => {
                        setPrivacyAccepted(event.target.checked);

                        clearMessages();
                      }}
                      className="mt-0.5 h-4 w-4 shrink-0 accent-[#ff6b35]"
                    />

                    <span className="text-xs leading-5 text-[#6b7280] sm:text-sm">
                      I agree to Chaafo&apos;s{" "}
                      <span className="font-bold text-[#374151]">
                        Privacy Policy
                      </span>{" "}
                      and consent to the processing of my profile details.
                    </span>
                  </label>

                  {successMessage && (
                    <div className="mt-4 flex items-start gap-2.5 rounded-[14px] border border-[#b8ebcf] bg-[#edfff4] px-3.5 py-3 text-xs font-semibold leading-5 text-[#157a44] sm:text-sm">
                      <CheckCircle2 size={18} className="mt-0.5 shrink-0" />

                      <span>{successMessage}</span>
                    </div>
                  )}

                  {visibleError && (
                    <div className="mt-4 break-words rounded-[14px] border border-[#fecaca] bg-[#fff1f1] px-3.5 py-3 text-xs font-semibold leading-5 text-[#c62828] sm:text-sm">
                      {visibleError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={
                      profileLoading ||
                      !firstName.trim() ||
                      !lastName.trim() ||
                      !privacyAccepted
                    }
                    className="group mt-6 flex h-[56px] w-full items-center justify-center gap-2.5 rounded-[15px] bg-[#111827] px-5 text-[15px] font-bold text-white shadow-[0_12px_30px_rgba(17,24,39,0.2)] transition hover:-translate-y-0.5 hover:bg-[#202939] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0"
                  >
                    {profileLoading ? (
                      <>
                        <Loader2 size={19} className="animate-spin" />
                        Saving profile...
                      </>
                    ) : (
                      <>
                        Complete profile
                        <ArrowRight
                          size={19}
                          className="transition group-hover:translate-x-1"
                        />
                      </>
                    )}
                  </button>

                  <div className="mt-6 flex items-start justify-center gap-2 text-center text-[10px] leading-4 text-[#8a9099] sm:text-xs sm:leading-5">
                    <ShieldCheck size={14} className="mt-0.5 shrink-0" />

                    <p>
                      Your profile information is protected with secure
                      authentication.
                    </p>
                  </div>
                </form>
              )}
            </div>
          </section>
        </div>
      </div>
    </main>
  );
};

export default ProfileDetails;
